import React, { useMemo } from "react";
import { ComparisonPeriod, KpiItem, resolveI18nLabel } from "./types";
import styles from "./KpiPanel.module.scss";
import { detectLanguageByTimezone } from "../../../../../utils/languageDetection";

interface KpiCardProps {
  kpi: KpiItem;
  isDarkTheme: boolean;
  selectedPeriod: ComparisonPeriod;
}

const formatValue = (
  value: number | string,
  valueType: KpiItem["valueType"],
): string => {
  if (valueType === "text" || valueType === "duration") {
    return String(value);
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return String(value);
  }

  if (valueType === "currency") {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  }

  if (valueType === "percentage") {
    return `${numValue}%`;
  }

  return new Intl.NumberFormat("pt-BR").format(numValue);
};

const getTrendIcon = (trend: KpiItem["trend"]): string => {
  if (trend === "up") return "fas fa-arrow-up";
  if (trend === "down") return "fas fa-arrow-down";
  return "fas fa-minus";
};

const comparisonLabels: Record<string, Record<string, string>> = {
  previous_day: { pt: "vs ontem", en: "vs yesterday", es: "vs ayer" },
  previous_period: { pt: "vs ontem", en: "vs yesterday", es: "vs ayer" },
  previous_week: {
    pt: "vs semana passada",
    en: "vs last week",
    es: "vs semana pasada",
  },
  previous_month: {
    pt: "vs mês passado",
    en: "vs last month",
    es: "vs mes pasado",
  },
};

const getComparisonLabel = (
  comparison: string | undefined,
  lang: string,
): string | undefined => {
  if (!comparison) return undefined;
  const labels = comparisonLabels[comparison];
  if (!labels) return undefined;
  return labels[lang] || labels["en"];
};

const KpiCard: React.FC<KpiCardProps> = ({
  kpi,
  isDarkTheme,
  selectedPeriod,
}) => {
  const lang = useMemo(() => detectLanguageByTimezone(), []);
  const label = resolveI18nLabel(kpi.label, lang);
  const description = kpi.description
    ? resolveI18nLabel(kpi.description, lang)
    : undefined;

  // Resolve values from trends map if available, otherwise fall back to root-level fields
  const trendData = kpi.trends?.[selectedPeriod];
  const displayValue = trendData?.value ?? kpi.value;
  const displayTrend = trendData?.trend ?? kpi.trend;
  const displayTrendValue = trendData?.trendValue ?? kpi.trendValue;
  const displayStatus = trendData?.status ?? kpi.status;

  const statusClass = styles[displayStatus] || "";
  const themeClass = !isDarkTheme ? styles.light : "";

  return (
    <div
      className={`${styles.kpiCard} ${statusClass} ${themeClass}`}
      title={description}
    >
      <div className={styles.kpiCardHeader}>
        {kpi.icon && (
          <span className={styles.kpiIcon}>
            <i className={kpi.icon} />
          </span>
        )}
        <span className={styles.kpiLabel}>{label}</span>
      </div>
      <div className={styles.kpiValue}>
        {formatValue(displayValue, kpi.valueType)}
      </div>
      {displayTrend && displayTrendValue && (
        <div
          className={`${styles.kpiTrend} ${styles[`trend-${displayTrend}`]}`}
        >
          <i className={getTrendIcon(displayTrend)} />
          <span className={styles.trendValue}>{displayTrendValue}</span>
          <span className={styles.trendLabel}>
            {getComparisonLabel(selectedPeriod, lang)}
          </span>
        </div>
      )}
    </div>
  );
};

export default KpiCard;
